import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete, Box, Button, Chip, IconButton, MenuItem, Select, Stack,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TextField, Typography,
} from '@mui/material'
import { AppNote, AppPillToggle, AppToolbarCreateButton, AppValueChip, secondaryButtonSx } from '@components'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { dsColorNew } from '@palette'
import { IconSeeker } from '@ui-seeker/core'
import { alpha } from '@mui/material/styles'
import { DeleteOutline as DeleteIcon, Add as AddIcon, ArrowRightAlt as ArrowRightIcon, LinkOutlined as LinkIcon, FiberManualRecord as CircleIcon, SubdirectoryArrowRight as SubdirIcon, LayersOutlined as LayersIcon, AutoFixHighOutlined as AutoFixIcon } from 'Pages/DsRepricing/@components/icons'

import { uid } from 'Pages/DsRepricing/Setup/@mocks'
import {
	useAxisCatalog, PREPAID_OPTIONS, prepaidKey, prepaidValue, idKey, idValue,
	useSetupCollection, usePricelists, usePricelistTiers, useCompensatorTarget,
	mergeTiers, tiersToSave, referenceLorOptions, setReferenceLor, tierKey, tierLabel,
	setupApi, USE_MOCKS,
	SiteCatalogProvider, useSiteCatalog,
} from 'Pages/DsRepricing/Setup/@services'
import { faceLabel } from 'Pages/DsRepricing/Setup/@services/faceLabel'
import AxisChip, { ALL_LABEL_BY_VARIANT } from 'Pages/DsRepricing/Setup/@components/AxisChip'
import { AppSitesAutocomplete } from '@components/Autocomplete'
import useToast from '@hooks/useToast'
import {
	Surface, SurfaceHeader, EmptyState, LoadingState, HelpNote,
} from 'Pages/DsRepricing/Setup/@components/Surface'
import { inputSx, rowHoverSx, cellPadSx, headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'
import useConfirm from 'Pages/DsRepricing/@components/useConfirm'

// Persistence: @services/useSetupCollection (entity 'rateCards').

/**
 * A "face" = one distinct configuration line for a site:
 *   (site, is_prepaid, ps_insurance_id, ps_provider_id) →
 *     read_pricelist  (ONE, the price the engine reads)
 *     write_primary   (ONE, the base target where the repriced value lands)
 *     write_derived[] (N, each one is base ± delta — one nesting level only)
 *
 * Derived writes let the operator publish the same base decision on
 * additional pricelists but shifted by a fixed % or absolute amount
 * (e.g. a "Genius +10%" list, a "-5€ mobile-only" list). The engine
 * computes base first, then derives each child. Derived lists cannot
 * themselves have children.
 */
const DEFAULT_FACES = [
	// Mock seed, only reachable behind `USE_MOCKS`. The three axes are
	// Price Seeker ids now and a mock has no way to invent real ones, so
	// only `is_prepaid` — which is a boolean and therefore knowable —
	// carries a value. `null` is "any" on the other two.
	{ id: uid(), site_id: null, is_prepaid: true,  ps_insurance_id: null, ps_provider_id: null,
	  read_pricelist: 'Discover Prepaid', write_primary: ['Discover Prepaid'],
	  write_derived: [], markup_limit: null },
	{ id: uid(), site_id: null, is_prepaid: false, ps_insurance_id: null, ps_provider_id: null,
	  read_pricelist: 'Discover', write_primary: ['Discover'],
	  write_derived: [], markup_limit: null },
	{ id: uid(), site_id: null, is_prepaid: true,  ps_insurance_id: null, ps_provider_id: null,
	  read_pricelist: 'Rentalcars Prepaid', write_primary: ['Rentalcars Prepaid'],
	  write_derived: [
	    { pricelists: ['Rentalcar Genius Pr'], delta: { k: 'pct', v: -10 } },
	  ], markup_limit: 15 },
]

// Migrate to current shape:
//   write_primary: string[]                              (many pricelists at base price)
//   write_derived: [{ pricelists: string[], delta }, …]  (many pricelists share one delta)
//
// Migrations covered:
//   - v1 (write_pricelists: [...])         → all into write_primary
//   - v2-interim (write_primary: string)   → wrap into array
//   - v3-interim (derived[i].pricelist:'') → wrap into pricelists:[…]
function migrateFace(f) {
	if (!f || typeof f !== 'object') return f
	let write_primary = f.write_primary
	if (write_primary === undefined) {
		write_primary = Array.isArray(f.write_pricelists) ? f.write_pricelists : []
	} else if (!Array.isArray(write_primary)) {
		write_primary = write_primary ? [write_primary] : []
	}
	const write_derived = (Array.isArray(f.write_derived) ? f.write_derived : []).map(d => {
		if (Array.isArray(d?.pricelists)) return d
		const one = d?.pricelist
		return { pricelists: one ? [one] : [], delta: d?.delta || { k: 'pct', v: 0 } }
	})
	const { write_pricelists, ...rest } = f
	return { ...rest, write_primary, write_derived,
	         markup_limit: rest.markup_limit ?? null }
}

// The three axis names used to be module-level lookups into hardcoded
// lists. They are tenant data now, so they come from `useAxisCatalog()`
// and the components read them from there.

function formatDelta(d) {
	if (!d || d.v == null) return ''
	const sign = d.v > 0 ? '+' : ''
	return d.k === 'pct' ? `${sign}${d.v}%` : `${sign}${d.v}€`
}

// Flat list of every write target (primary + derived) of a face, for
// the shared-tariff detector. Each entry: { code, kind: 'primary'|'derived' }.
function allWrites(face) {
	const out = []
	for (const pl of face.write_primary || []) {
		if (pl) out.push({ code: pl, kind: 'primary' })
	}
	for (const d of face.write_derived || []) {
		for (const pl of d.pricelists || []) {
			if (pl) out.push({ code: pl, kind: 'derived' })
		}
	}
	return out
}

// How the operator knows a rate card: the shared `faceLabel` of
// `Setup/@services/faceLabel` (UX obligatory 1, CU-869ffwj71; one name
// everywhere, CU-869fh646w D1), called with `{ elide: true }` so the
// blank axes are left out — "Sunny Cars", never "Sunny Cars · — · — · —".

//: "A and B" / "A, B and C" — the rate cards that share a tariff.
function joinNames(names) {
	if (names.length <= 1) return names.join('')
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

function RateCardsTabInner() {
	// Persisted through webback (or the localStorage mock behind the flag).
	// Pricelist codes ↔ ids are resolved by the adapters with the tenant
	// catalogue provided by Setup/index.jsx.
	const siteCatalog = useSiteCatalog()
	const axes = useAxisCatalog()
	const [faces, setFaces, sync] = useSetupCollection('rateCards', {
		mockDefault: DEFAULT_FACES, normalise: migrateFace,
	})
	const pricelists = usePricelists()
	const [siteFilter, setSiteFilter] = useState('*')
	const [franchiseFilter, setFranchiseFilter] = useState('*')
	const [searchTerm, setSearchTerm] = useState('')

	function addFace() {
		setFaces(prev => [{
			id: uid(),
			site_id: siteFilter !== '*' ? Number(siteFilter) : null,
			// "Any" on all three: a new face pins nothing until the
			// operator says so, and the most specific face wins anyway.
			is_prepaid: null,
			ps_insurance_id: null,
			ps_provider_id: franchiseFilter !== '*' && franchiseFilter !== '__none__'
				? Number(franchiseFilter) : null,
			read_pricelist: null,
			write_primary: [],
			write_derived: [],
			markup_limit: null,
		}, ...prev])
	}
	function updateFace(id, patch) {
		setFaces(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f))
	}
	// Changing the read pricelist re-anchors the duration tiers: the new
	// pricelist's canonical brackets, each prefilled with its default
	// reference LOR, keeping any pick whose bracket survives (PRD 01 §3).
	function changeReadPricelist(face, code) {
		const canonical = (pricelists.find(p => p.code === code)?.tiers) || []
		updateFace(face.id, {
			read_pricelist: code,
			tier_config: tiersToSave(mergeTiers(canonical, face.tier_config || [])),
		})
	}
	// The bin asks first (K1, CU-869ffwj71): the dialog names the rate
	// card, what it reads and writes, warns that its reference LOR per
	// bracket go with it, and says what happens to a shared tariff.
	const [confirmDelete, confirmDialog] = useConfirm()
	async function deleteFace(id) {
		const f = faces.find(x => x.id === id)
		if (!f) return
		const name = faceLabel(f, siteCatalog, axes, { elide: true })
		const writes = [...new Set(allWrites(f).map(w => w.code))]
		const stillShared = writes
			.map(code => ({ code, others: (writers[code] || []).filter(w => w.id !== id) }))
			.filter(x => x.others.length > 0)
		const ok = await confirmDelete({
			title: 'Delete this rate card?',
			body: (
				<>
					<strong>{name}</strong> stops reading{' '}
					<code>{f.read_pricelist || '—'}</code> and writing{' '}
					{writes.length ? writes.map((c, i) => <React.Fragment key={c}>{i > 0 && ', '}<code>{c}</code></React.Fragment>) : '—'}.
					Its reference LOR per bracket is lost; recreating it means setting them again.
				</>
			),
			detail: stillShared.length
				? stillShared.map(x => (
					<Box key={x.code}>
						<code>{x.code}</code> keeps being written by{' '}
						{joinNames([...new Set(x.others.map(w => w.label))])}.
					</Box>
				  ))
				: 'Nothing else changes.',
			confirmLabel: 'Delete rate card',
			destructive: true,
		})
		if (!ok) return
		setFaces(prev => prev.filter(x => x.id !== id))
	}

	// Detect duplicate faces (same tuple), and which rate cards write each
	// pricelist (primary or derived). Grouped by rate card, not by site:
	// two faces of the same site may share a tariff, and they are marked
	// like any other pair (UX obligatory 3, CU-869ffwj71). Local, on the
	// loaded collection — no extra call.
	const { duplicates, writers, sharedWrites } = useMemo(() => {
		const tupleMap = new Map()
		const writers = {}
		for (const f of faces) {
			// `??` and not `||`: `false` is Non Prepaid, and collapsing it
			// into the "any" bucket would call two different faces the same.
			const key = [f.site_id, f.is_prepaid ?? '-',
			             f.ps_insurance_id ?? '-', f.ps_provider_id ?? '-'].join('|')
			tupleMap.set(key, (tupleMap.get(key) || 0) + 1)
			const label = faceLabel(f, siteCatalog, axes, { elide: true })
			for (const w of allWrites(f)) {
				const list = writers[w.code] || (writers[w.code] = [])
				if (!list.some(x => x.id === f.id)) list.push({ id: f.id, label, kind: w.kind })
			}
		}
		const duplicates = new Set(
			[...tupleMap.entries()].filter(([, n]) => n > 1).map(([k]) => k))
		// `{code: [{id, label, kind}, …]}` for the pricelists more than
		// one rate card writes.
		const sharedWrites = {}
		for (const [code, list] of Object.entries(writers)) {
			if (list.length > 1) sharedWrites[code] = list
		}
		return { duplicates, writers, sharedWrites }
	}, [faces, siteCatalog, axes])

	const visible = faces.filter(f => {
		if (siteFilter !== '*' && String(f.site_id) !== String(siteFilter)) return false
		if (franchiseFilter !== '*') {
			if (franchiseFilter === '__none__' && f.ps_provider_id != null) return false
			if (franchiseFilter !== '__none__'
			    && String(f.ps_provider_id) !== String(franchiseFilter)) return false
		}
		if (searchTerm) {
			const hay = [
				siteCatalog.labelOf(f.site_id),
				axes.prepaidName(f.is_prepaid),
				axes.insuranceName(f.ps_insurance_id),
				axes.franchiseName(f.ps_provider_id),
				f.read_pricelist,
				...(f.write_primary || []),
				...(f.write_derived || []).flatMap(d => d.pricelists || []),
			].filter(Boolean).join(' ').toLowerCase()
			if (!hay.includes(searchTerm.toLowerCase())) return false
		}
		return true
	})

	const totalWrites = faces.reduce(
		(s, f) => s + (f.write_primary?.length || 0)
		            + (f.write_derived || []).reduce((n, d) => n + (d.pricelists?.length || 0), 0),
		0)
	const derivedCount = faces.reduce(
		(s, f) => s + (f.write_derived || []).reduce((n, d) => n + (d.pricelists?.length || 0), 0),
		0)

	return (
		<Surface padded={false}>
			<Box sx={{ p: 2.5 }}>
				<SurfaceHeader
					// The card's notes, with its description, behind the ⓘ of its title
					info={<>
						<Box>
						<strong>Blank means any.</strong> A rate card with no tariff type
						or insurance covers every kind on that axis: in each cell our
						cheapest row is the one priced, and that row's own tariff type
						and insurance decide who it is compared against. What a strategy
						prices, and whether it compares against every kind, is set in{' '}
						<strong>Strategies</strong>, not here.
						{' '}<strong>Primary vs derived.</strong> The Primary write is where
						the repriced value lands as-is. Every Derived write publishes the
						same base decision with a fixed <em>+/− %</em> or <em>+/− €</em>
						{' '}offset — useful for parallel channels (mobile, loyalty,
						B2B) that always run a fixed spread over the main list.
						{' '}<strong>Only one nesting level:</strong> derived writes
						can't have their own children.
						{' '}<strong>Markup cap</strong> is a per-face safety net:
						the pushed price never exceeds the read pricelist by more than
						that percentage. Empty means no cap.
						{' '}<strong>Shared tariffs.</strong> Several rate cards may write
						the same pricelist (primary or derived); the table marks it. When
						two of them decide the same bracket, the decision of the strategy
						with the higher priority (the lower number) is sent; on a tie, the
						one that moves the price more. The others are kept in the Activity
						as <strong>outranked</strong>, never sent. One net per tariff:
						every broker publishes it with its own markup. Each rate card
						launches its own rate shops, so a second rate card on a tariff
						adds searches to the bill.
						</Box>
					</>}
					icon="tags"
					title="Rate cards (Tarifas)"
					subtitle={<>Each row is a <strong>face</strong> of a site:{' '}
					<em>site + tariff type + insurance type + franchise</em>. Points to
					{' '}<strong>ONE Read</strong> pricelist (the price the engine reads),
					{' '}<strong>ONE Primary Write</strong> (base target) and any number
					of <strong>Derived Writes</strong> that publish the same decision
					with a <em>fixed ± offset</em> (%, €). One nesting level only.</>}
					actions={
						<Stack direction="row" gap={1} flexWrap="wrap">
							<KpiBox label="Faces"   value={faces.length}/>
							<KpiBox label="Sites"   value={new Set(faces.map(f => f.site_id)).size}/>
							<KpiBox label="Writes"  value={totalWrites}/>
							<KpiBox label="Derived" value={derivedCount}/>
						</Stack>
					}
				/>

				<Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap"
				       sx={{ mb: 2 }}>
					<Select size="small" value={siteFilter}
					        onChange={(e) => setSiteFilter(e.target.value)}
					        sx={{ minWidth: 180, ...inputSx }}>
						<MenuItem value="*"><em>All sites</em></MenuItem>
						{siteCatalog.options.map(s => (
							<MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
						))}
					</Select>
					<Select size="small" value={franchiseFilter}
					        onChange={(e) => setFranchiseFilter(e.target.value)}
					        sx={{ minWidth: 180, ...inputSx }}>
						<MenuItem value="*"><em>All franchises</em></MenuItem>
						<MenuItem value="__none__"><em style={{ color: dsColorNew.textMuted }}>No franchise (ALL)</em></MenuItem>
						{axes.franchises.map(fr => (
							<MenuItem key={fr.code} value={fr.code}>{fr.name}</MenuItem>
						))}
						{!axes.loading && axes.franchises.length === 0 && (
							<MenuItem disabled>
								<em>No own brands configured for this tenant</em>
							</MenuItem>
						)}
					</Select>
					<TextField size="small" placeholder="Search face, pricelist code…"
					           value={searchTerm}
					           onChange={(e) => setSearchTerm(e.target.value)}
					           sx={{ minWidth: 280, ...inputSx }}/>
					<Box sx={{ flex: 1 }}/>
					<AppToolbarCreateButton divider={false} label="Add face" icon="plus"
					                        onClick={addFace} sx={{ height: 38 }}/>
				</Stack>

				{confirmDialog}
				{Object.keys(sharedWrites).length > 0 && (
					<SharedTariffsNote sharedWrites={sharedWrites} pricelists={pricelists}/>
				)}

				{sync.loading ? (
					<LoadingState what="rate cards"/>
				) : visible.length === 0 ? (
					<EmptyState
						icon={<LinkIcon/>}
						title="No faces yet"
						description="Add one face per site × tariff × insurance × franchise combination. Each face has one Read pricelist, one Primary Write, and optional Derived Writes with a % or € delta."
						action={
							<Button variant="outlined" startIcon={<AddIcon/>}
							        onClick={addFace}
							        sx={{ ...secondaryButtonSx, height: 36 }}>
								Add first face
							</Button>
						}
					/>
				) : (
					<TableContainer sx={{ border: '1px solid', borderColor: dsColorNew.grayBorder,
					                      borderRadius: '10px' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									{/* Sites are named things — "discovercars", "holidayautos",
									    "canarias.com" — and at 140 every one of them was an
									    ellipsis. The column is the widest here because it is the
									    only one whose values are not a short closed vocabulary. */}
									<HeadCell width={260}>Site</HeadCell>
									<HeadCell width={130}>Tariff type</HeadCell>
									<HeadCell width={150}>Insurance</HeadCell>
									<HeadCell width={140}>Franchise</HeadCell>
									<HeadCell width={220}>
										<Stack direction="row" gap={0.5} alignItems="center">
											<ArrowRightIcon sx={{ fontSize: 16, transform: 'rotate(180deg)',
											                       color: dsColorNew.appBlue }}/>
											<span>Read pricelist</span>
										</Stack>
									</HeadCell>
									<HeadCell align="right" width={110}>
										<span title="Max % that the pushed price may exceed the read pricelist. Empty = no cap.">
											Markup cap
										</span>
									</HeadCell>
									<HeadCell>
										<Stack direction="row" gap={0.5} alignItems="center">
											<ArrowRightIcon sx={{ fontSize: 16, color: dsColorNew.successText }}/>
											<span>Write · primary + derived</span>
										</Stack>
									</HeadCell>
									<HeadCell width={90}></HeadCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{visible.map(f => {
									const tupleKey = [f.site_id, f.is_prepaid ?? '-',
									                  f.ps_insurance_id ?? '-',
									                  f.ps_provider_id ?? '-'].join('|')
									const isDup = duplicates.has(tupleKey)
									return (
										<TableRow key={f.id}
										          sx={{
										              ...rowHoverSx,
										              verticalAlign: 'top',
										              ...(isDup && { bgcolor: dsColorNew.noteAmberBg }),
										          }}>
											<TableCell sx={cellPadSx}>
												{/* Nothing here sizes the dropdown: MUI anchors the popper
												    to the input, so widening the column widens the list
												    too. That matters as much as the field — at 140 the
												    operator could not read the names in the list they were
												    choosing from either. */}
												<AppSitesAutocomplete multiple={false} label="" placeholder="Site"
												                      value={f.site_id}
												                      onChange={(e) => updateFace(f.id, { site_id: e.target.value == null ? null : Number(e.target.value) })}
												                      size="small"/>
											</TableCell>
											{/* Every axis travels as a Price Seeker id (or a
											    boolean) and is rendered through a string key, so
											    that `false` and a hypothetical id 0 survive: an
											    empty key is the only "any". */}
											<TableCell sx={cellPadSx}>
												<EnumSelect value={prepaidKey(f.is_prepaid)}
												            options={PREPAID_OPTIONS}
												            allowNull
												            onChange={(v) => updateFace(f.id, { is_prepaid: prepaidValue(v) })}
												            variant="tariff"/>
											</TableCell>
											<TableCell sx={cellPadSx}>
												<EnumSelect value={idKey(f.ps_insurance_id)}
												            options={axes.insurances}
												            allowNull
												            onChange={(v) => updateFace(f.id, { ps_insurance_id: idValue(v) })}
												            variant="insurance"/>
											</TableCell>
											<TableCell sx={cellPadSx}>
												<EnumSelect value={idKey(f.ps_provider_id)}
												            options={axes.franchises}
												            allowNull
												            onChange={(v) => updateFace(f.id, { ps_provider_id: idValue(v) })}
												            variant="franchise"/>
											</TableCell>
											<TableCell sx={cellPadSx}>
												<ReadPricelistPicker face={f}
												                     value={f.read_pricelist}
												                     onChange={(v) => changeReadPricelist(f, v)}/>
												<TierEditor face={f}
												            onChange={(tier_config) => updateFace(f.id, { tier_config })}/>
											</TableCell>
											<TableCell align="right" sx={cellPadSx}>
												<MarkupCapInput
													value={f.markup_limit}
													onChange={(v) => updateFace(f.id, { markup_limit: v })}/>
											</TableCell>
											<TableCell sx={cellPadSx}>
												<WriteStack face={f}
												            writers={writers}
												            onChangePrimary={(v) => updateFace(f.id, { write_primary: v })}
												            onChangeDerived={(v) => updateFace(f.id, { write_derived: v })}/>
											</TableCell>
											<TableCell sx={cellPadSx}>
												<Stack direction="row" gap={0.25} alignItems="center"
												       justifyContent="center" sx={{ pt: 0.5 }}>
													<UnsavedChip refused={sync.refused?.get(f.id)}
													             stalled={sync.stalled?.has(f.id)}
													             reason={sync.held?.has(f.id)
													               ? 'Pick a write pricelist and it saves itself.' : null}/>
													<IconButton size="small"
													            title="Add derived write"
													            onClick={() => updateFace(f.id, {
														            write_derived: [...(f.write_derived || []),
														              { pricelists: [], delta: { k: 'pct', v: 0 } }],
													            })}
													            sx={{ color: dsColorNew.successText,
													                  border: `1px solid ${alpha(dsColorNew.successBar, 0.4)}`,
													                  bgcolor: dsColorNew.successBg,
													                  borderRadius: '8px',
													                  '&:hover': { color: dsColorNew.successText,
													                               bgcolor: dsColorNew.successBg,
													                               borderColor: dsColorNew.successBar } }}>
														<AddIcon fontSize="small"/>
													</IconButton>
													<IconButton size="small"
													            title="Delete rate card"
													            onClick={() => deleteFace(f.id)}
													            sx={{ color: dsColorNew.textMuted,
													                  '&:hover': { color: dsColorNew.dangerRed,
													                               bgcolor: dsColorNew.dangerBg } }}>
														<DeleteIcon fontSize="small"/>
													</IconButton>
												</Stack>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}

			</Box>
		</Surface>
	)
}

// ---------------------------------------------------------------------------
// Write stack — primary(N) + derived(N)
// ---------------------------------------------------------------------------
/**
 * Duration tiers of the face's read pricelist (PRD 01 §3).
 *
 * The brackets come from the PMS (`dim_pricelist_tier`, synced daily); the
 * operator only picks the **reference LOR** inside each bracket — the
 * length of rental the engine rate-shops with for that bracket. Default is
 * the top of the range.
 *
 * Drift: brackets that survive keep their pick, new ones arrive prefilled
 * and flagged, ones the PMS dropped are struck through and purged when the
 * face is saved.
 */
function TierEditor({ face, onChange }) {
	const canonical = usePricelistTiers(face.read_pricelist)
	const rows = useMemo(() => mergeTiers(canonical, face.tier_config || []),
	                     [canonical, face.tier_config])
	const stale = rows.some(r => r.status !== 'ok')

	if (!face.read_pricelist) return null
	if (!canonical.length) {
		return (
			<Typography sx={{ mt: 0.75, fontSize: 11, color: dsColorNew.textSubtle }}>
				{USE_MOCKS
					? 'Tier structure comes from the PMS sync — not available in mock mode.'
					: 'No tier structure synced for this pricelist yet.'}
			</Typography>
		)
	}

	return (
		<Box sx={{ mt: 0.75, pl: 0.25 }}>
			<Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.25 }}>
				<LayersIcon sx={{ fontSize: 13, color: dsColorNew.textSubtle }}/>
				<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted, fontWeight: 600 }}>
					Reference LOR per tier
				</Typography>
				{stale && (
					<Chip size="small" label="review" sx={toneChipSx('warn')}/>
				)}
			</Stack>
			<Stack gap={0.25}>
				{rows.map(r => {
					const key = tierKey(r)
					const gone = r.status === 'gone'
					return (
						<Stack key={key} direction="row" alignItems="center" gap={0.5}>
							<Typography sx={{
								minWidth: 54, fontSize: 11,
								fontFamily: 'inherit',
								color: gone ? dsColorNew.textSubtle : dsColorNew.textMuted,
								textDecoration: gone ? 'line-through' : 'none',
							}}>
								{tierLabel(r)} d
							</Typography>
							{gone ? (
								<Typography sx={{ fontSize: 11, color: dsColorNew.textSubtle }}>
									dropped by the PMS — removed on save
								</Typography>
							) : (
								<>
									<Select size="small"
									        value={r.reference_lor}
									        onChange={(e) => onChange(tiersToSave(
									          setReferenceLor(rows, key, Number(e.target.value))))}
									        sx={{ ...inputSx, minWidth: 64,
									              '& .MuiSelect-select': { py: 0.25, fontSize: 11 },
									              ...(r.status === 'new' && { bgcolor: dsColorNew.successBg }) }}>
										{referenceLorOptions(r).map(lor => (
											<MenuItem key={lor} value={lor} sx={{ fontSize: 11.5 }}>
												{lor} d
											</MenuItem>
										))}
									</Select>
									{r.status === 'new' && (
										<Typography sx={{ fontSize: 11, color: dsColorNew.successText }}>new</Typography>
									)}
								</>
							)}
						</Stack>
					)
				})}
			</Stack>
			{stale && (
				<Button size="small" variant="text"
				        onClick={() => onChange(tiersToSave(rows))}
				        sx={{ mt: 0.25, fontSize: 11, py: 0 }}>
					Reconcile with the PMS structure
				</Button>
			)}
		</Box>
	)
}

function WriteStack({ face, writers, onChangePrimary, onChangeDerived }) {
	const derived = face.write_derived || []
	// The read pricelist may be one the PMS derives from another (e.g.
	// DISCOVERCAR = WEB × 1.15). Those cannot be written directly: the face
	// writes the BASE with the inverse delta, which the PMS's own markup
	// cancels out (PRD 01 §3 "Pricelists heredadas del PMS").
	const compensatorTarget = useCompensatorTarget(face.read_pricelist)
	const { showToast } = useToast()
	const [adding, setAdding] = useState(false)
	const alreadyCompensated = derived.some(d => (d.pricelists || []).includes(compensatorTarget))

	async function addCompensator() {
		setAdding(true)
		try {
			const hint = await setupApi.inheritedHint(face.read_pricelist)
			if (!hint?.base_pricelist_code || hint.compensating_delta_pct == null) {
				showToast.info('This pricelist is not derived by the PMS')
				return
			}
			onChangeDerived([...derived, {
				pricelists: [hint.base_pricelist_code],
				delta: { k: 'pct', v: Number(hint.compensating_delta_pct) },
			}])
		} catch (e) {
			showToast.error(e?.message || 'Could not read the inherited-pricelist hint')
		} finally {
			setAdding(false)
		}
	}

	function updateDerivedAt(idx, patch) {
		onChangeDerived(derived.map((d, i) => i === idx ? { ...d, ...patch } : d))
	}
	function removeDerivedAt(idx) {
		onChangeDerived(derived.filter((_, i) => i !== idx))
	}

	return (
		<Stack gap={0.75}>
			<PrimaryPicker value={face.write_primary || []}
			               writers={writers} faceId={face.id}
			               onChange={onChangePrimary}/>
			{derived.map((d, i) => (
				<DerivedRow key={i}
				            value={d}
				            writers={writers} faceId={face.id}
				            compensatorTarget={compensatorTarget}
				            onChange={(patch) => updateDerivedAt(i, patch)}
				            onRemove={() => removeDerivedAt(i)}/>
			))}
			{!USE_MOCKS && compensatorTarget && !alreadyCompensated && (
				<Button size="small" variant="text" startIcon={<AutoFixIcon sx={{ fontSize: 14 }}/>}
				        disabled={adding}
				        onClick={addCompensator}
				        sx={{ alignSelf: 'flex-start', fontSize: 11,
				              py: 0, color: dsColorNew.successText }}>
					Add compensator on {compensatorTarget}
				</Button>
			)}
		</Stack>
	)
}

// The other rate cards that write a pricelist — this face excluded.
// Sharing a tariff is a legal configuration, so the chip stays green and
// gains a mark and a title; nothing is amber here any more (CU-869ffwj71).
const othersWriting = (writers, faceId, code) =>
	(writers?.[code] || []).filter(w => w.id !== faceId)

const sharedTitle = (others) =>
	`Also written by ${joinNames([...new Set(others.map(w => w.label))])}. When both decide the same bracket, the higher-priority strategy sets the price; on a tie, the bigger move.`

//: A two-way arrow and `shared` after the code, in the chip.
function sharedChipLabel(code, shared) {
	if (!shared) return code
	return (
		<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
			{code}
			<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontFamily: 'inherit', fontWeight: 700, fontSize: 11,
			                            color: dsColorNew.successText, borderLeft: `1px solid ${alpha(dsColorNew.successBar, 0.4)}`, pl: 0.5 }}>
				<IconSeeker icon="arrow-right-arrow-left" sx={{ fontSize: 10 }}/>shared
			</Box>
		</Box>
	)
}

//: The picker says who else writes a pricelist before the operator picks
//: it — informative, never disabled, never hidden (UX recommended).
function alsoWrittenBy(others) {
	if (!others.length) return null
	const derived = others.every(w => w.kind === 'derived')
	return (
		<Typography sx={{ ml: 'auto', pl: 1, fontSize: 11.5, fontWeight: 600,
		                  color: dsColorNew.appBlue, whiteSpace: 'nowrap' }}>
			also written by {joinNames([...new Set(others.map(w => w.label))])}{derived ? ' (derived)' : ''}
		</Typography>
	)
}

function PrimaryPicker({ value, writers, faceId, onChange }) {
	const PRICELISTS = usePricelists()
	const selected = PRICELISTS.filter(o => value.includes(o.code))
	return (
		<Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
			<Chip label="PRIMARY" size="small"
			      sx={{ height: 22, fontSize: 11, fontWeight: 600, borderRadius: '6px',
			            letterSpacing: '0.06em', minWidth: 68, mt: 0.25,
			            bgcolor: dsColorNew.successBg, color: dsColorNew.successText }}/>
			<Box sx={{ flex: 1 }}>
				<Autocomplete multiple size="small"
				              options={PRICELISTS}
				              value={selected}
				              getOptionLabel={(o) => o.code}
				              isOptionEqualToValue={(a, b) => a.code === b.code}
				              onChange={(_, next) => onChange(next.map(o => o.code))}
				              renderTags={(v, getTagProps) =>
				                v.map((o, idx) => {
				                  const others = othersWriting(writers, faceId, o.code)
				                  const shared = others.length > 0
				                  return (
				                    <Chip {...getTagProps({ index: idx })}
				                          key={o.code} size="small"
				                          label={sharedChipLabel(o.code, shared)}
				                          title={shared ? sharedTitle(others) : undefined}
				                          sx={{
				                              fontFamily: 'inherit',
				                              fontSize: 11, fontWeight: 700,
				                              bgcolor: dsColorNew.successBg,
				                              color:   dsColorNew.successText,
				                              border: '1px solid',
				                              borderColor: alpha(dsColorNew.successBar, 0.4),
				                              '& .MuiChip-deleteIcon': { color: dsColorNew.successText },
				                          }}/>
				                  )
				                })}
				              renderOption={(props, o) => (
				                <li {...props} key={o.code}>
				                  <Box component="code" sx={{ mr: 1, fontWeight: 700,
				                                              minWidth: 130,
				                                              fontFamily: 'inherit',
				                                              fontSize: 11.5 }}>
				                    {o.code}
				                  </Box>
				                  <Typography sx={{ fontSize: 12,
				                                    color: dsColorNew.textMuted }}>
				                    {o.name}
				                  </Typography>
				                  {alsoWrittenBy(othersWriting(writers, faceId, o.code))}
				                </li>
				              )}
				              renderInput={(params) => (
				                <TextField {...params}
				                           placeholder={value.length ? '' : 'Add primary write(s)…'}
				                           sx={inputSx}/>
				              )}/>
			</Box>
		</Box>
	)
}

function DerivedRow({ value, writers, faceId, compensatorTarget, onChange, onRemove }) {
	const siteCatalog = useSiteCatalog()
	const PRICELISTS = usePricelists()
	const codes = value.pricelists || []
	// A delta that compensates a PMS derivation must be a percentage: a
	// euro delta does not scale with the price and breaks the compensation
	// as soon as the target amount moves (PRD 01 §3 "Regla de UI").
	const forcePct = !!compensatorTarget && codes.includes(compensatorTarget)
	// Keep state coherent with what the UI shows: if the operator points a
	// euro-delta row at the compensated base, the row becomes a percentage.
	useEffect(() => {
		if (forcePct && (value.delta?.k || 'pct') !== 'pct') {
			onChange({ delta: { k: 'pct', v: value.delta?.v ?? 0 } })
		}
	}, [forcePct])
	const selected = PRICELISTS.filter(o => codes.includes(o.code))
	const k = value.delta?.k || 'pct'
	const v = value.delta?.v ?? 0
	return (
		<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pl: 1.5 }}>
			<SubdirIcon sx={{ fontSize: 16, color: dsColorNew.textSubtle, mr: 0.25 }}/>
			<Box sx={{ flex: 1 }}>
				<Autocomplete multiple size="small"
				              options={PRICELISTS}
				              value={selected}
				              getOptionLabel={(o) => o.code}
				              isOptionEqualToValue={(a, b) => a.code === b.code}
				              onChange={(_, next) => onChange({ pricelists: next.map(o => o.code) })}
				              renderTags={(list, getTagProps) =>
				                list.map((o, idx) => {
				                  const others = othersWriting(writers, faceId, o.code)
				                  const shared = others.length > 0
				                  return (
				                    <Chip {...getTagProps({ index: idx })}
				                          key={o.code} size="small"
				                          label={sharedChipLabel(o.code, shared)}
				                          title={shared ? sharedTitle(others) : undefined}
				                          sx={{
				                              fontFamily: 'inherit',
				                              fontSize: 11, fontWeight: 700,
				                              bgcolor: dsColorNew.successBg,
				                              color:   dsColorNew.successText,
				                              border: '1px solid',
				                              borderColor: alpha(dsColorNew.successBar, 0.4),
				                              '& .MuiChip-deleteIcon': { color: dsColorNew.successText },
				                          }}/>
				                  )
				                })}
				              renderInput={(params) => (
				                <TextField {...params}
				                           placeholder={codes.length ? '' : 'Add derived write(s) that share this delta…'}
				                           sx={inputSx}/>
				              )}
				              renderOption={(props, o) => (
				                <li {...props} key={o.code}>
				                  <Box component="code" sx={{ mr: 1, fontWeight: 700,
				                                              minWidth: 130,
				                                              fontFamily: 'inherit',
				                                              fontSize: 11.5 }}>
				                    {o.code}
				                  </Box>
				                  <Typography sx={{ fontSize: 12,
				                                    color: dsColorNew.textMuted }}>
				                    {o.name}
				                  </Typography>
				                  {alsoWrittenBy(othersWriting(writers, faceId, o.code))}
				                </li>
				              )}/>
			</Box>
			<TextField type="number" size="small"
			           value={v}
			           onChange={(e) => onChange({
			               delta: { k, v: e.target.value === '' ? 0 : Number(e.target.value) },
			           })}
			           inputProps={{
			               style: { width: 60, textAlign: 'right',
			                        fontWeight: 700, fontSize: 13.5,
			                        padding: '6px 8px' },
			           }}
			           sx={{ ...inputSx,
			                 '& .MuiOutlinedInput-root': { height: 34 } }}/>
			<UnitToggle value={forcePct ? 'pct' : k}
			            disabled={forcePct}
			            title={forcePct
			                ? compensatorTarget + ' is the base of this face\'s read pricelist — a compensating delta must be a percentage'
			                : undefined}
			            onChange={(next) => onChange({ delta: { k: next, v } })}/>
			<Chip label={formatDelta(value.delta)} size="small"
			      sx={{ height: 34, fontSize: 12.5, fontWeight: 700,
			            minWidth: 60, borderRadius: '8px',
			            bgcolor: v === 0 ? dsColorNew.pillTrackBg
			                   : v > 0    ? dsColorNew.successBg : dsColorNew.dangerBg,
			            color:   v === 0 ? dsColorNew.textMuted
			                   : v > 0    ? dsColorNew.successText : dsColorNew.dangerRed,
			            border: '1px solid',
			            borderColor: v === 0 ? dsColorNew.grayBorder
			                       : v > 0    ? alpha(dsColorNew.successBar, 0.4) : dsColorNew.dangerBorder }}/>
			<IconButton size="small" onClick={onRemove}
			            sx={{ color: dsColorNew.textMuted,
			                  '&:hover': { color: dsColorNew.dangerRed } }}>
				<DeleteIcon fontSize="small"/>
			</IconButton>
		</Box>
	)
}

// Numeric input for the per-face markup cap. Empty = no cap (null).
// The trailing % is rendered inside the field via endAdornment so the
// value stays right-aligned. Negative and zero are allowed as-is —
// zero literally means "never markup above the read price".
function MarkupCapInput({ value, onChange }) {
	const hasCap = value != null && value !== ''
	return (
		<TextField type="number" size="small"
		           value={value ?? ''}
		           onChange={(e) => {
		               const raw = e.target.value
		               onChange(raw === '' ? null : Number(raw))
		           }}
		           placeholder="no cap"
		           InputProps={{
		               endAdornment: (
		                   <Box component="span" sx={{
		                       fontWeight: 700, color: dsColorNew.textMuted,
		                       fontSize: 12, pl: 0.25,
		                   }}>
		                       %
		                   </Box>
		               ),
		           }}
		           inputProps={{ style: { width: 44, textAlign: 'right',
		                                  fontWeight: hasCap ? 800 : 400,
		                                  fontSize: 13 } }}
		           sx={{ ...inputSx,
		                 '& .MuiOutlinedInput-root': {
		                     bgcolor: hasCap ? dsColorNew.noteAmberBg : dsColorNew.white,
		                     '&:hover fieldset': { borderColor: hasCap ? dsColorNew.warningBar : undefined },
		                 } }}/>
	)
}

// The %/€ unit of a delta: the app's pill toggle. While the delta compensates a PMS derivation it is
// locked on % — the title says why, and a click changes nothing.
const UNIT_OPTIONS = [{ value: 'pct', label: '%' }, { value: 'abs', label: '€' }]

function UnitToggle({ value, onChange, disabled = false, title }) {
	return (
		<Box title={title} sx={{ display: 'inline-flex', flexShrink: 0,
		                         ...(disabled && { opacity: 0.6, '& button': { cursor: 'not-allowed' } }) }}>
			<AppPillToggle size="small" value={value} options={UNIT_OPTIONS}
			               aria-disabled={disabled || undefined}
			               onChange={(next) => !disabled && onChange(next)}/>
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Sub-components (unchanged)
// ---------------------------------------------------------------------------
function EnumSelect({ value, options, onChange, variant, allowNull = false }) {
	// Blank means any on every axis (CU-869fh646w): a rate card that
	// leaves tariff type or insurance blank covers every kind on it and
	// our cheapest row of each cell is the one priced. The chip says
	// "All …" with no title; the help note of the tab says what blank
	// means, once. The menu item keeps "All …" too: it is the choice.
	const allLabel = ALL_LABEL_BY_VARIANT[variant] || 'All'
	return (
		<Select size="small" fullWidth displayEmpty
		        value={value ?? ''}
		        onChange={(e) => onChange(e.target.value)}
		        renderValue={(v) => {
		          if (!v) return <AxisChip variant={variant} blank label={allowNull ? allLabel : '—'}/>
		          const opt = options.find(o => o.code === v)
		          return <AxisChip variant={variant} label={opt?.name || v}/>
		        }}
		        sx={inputSx}>
			{allowNull && (
				<MenuItem value="">
					<em style={{ color: dsColorNew.textMuted }}>{allLabel}</em>
				</MenuItem>
			)}
			{options.map(o => (
				<MenuItem key={o.code} value={o.code}>{o.name}</MenuItem>
			))}
		</Select>
	)
}

function ReadPricelistPicker({ value, onChange, face }) {
	const PRICELISTS = usePricelists()
	const axes = useAxisCatalog()
	const options = PRICELISTS
	const selected = options.find(o => o.code === value) || null
	// Which pricelists to highlight, from what the face is pinned to.
	// This used to be a table of hardcoded slugs ('cgu' → 'cug'), which
	// only ever knew canarias.com's vocabulary. The axis names are tenant
	// data now, so the keywords are the names themselves: a face on
	// "Tenerifecar" highlights every pricelist with that word in it, for
	// any tenant, with nothing to maintain. Words under three letters are
	// dropped — 'a' would suggest everything.
	const hint = useMemo(() => {
		const words = (s) => String(s || '').toLowerCase()
			.split(/[^a-z0-9]+/).filter(w => w.length >= 3)
		const kw = []
		if (face.is_prepaid === true)  kw.push('prepaid')
		if (face.ps_insurance_id != null) kw.push(...words(axes.insuranceName(face.ps_insurance_id)))
		if (face.ps_provider_id != null)  kw.push(...words(axes.franchiseName(face.ps_provider_id)))
		return [...new Set(kw)]
	}, [face.is_prepaid, face.ps_insurance_id, face.ps_provider_id,
	    axes.insurances, axes.franchises])
	const isSuggested = (o) => hint.length > 0 && hint.some(k =>
		o.name.toLowerCase().includes(k) || o.code.toLowerCase().includes(k))

	return (
		<Autocomplete size="small"
		              options={options}
		              value={selected}
		              getOptionLabel={(o) => o.code}
		              isOptionEqualToValue={(a, b) => a.code === b.code}
		              onChange={(_, next) => onChange(next?.code ?? null)}
		              renderOption={(props, o) => (
		                <li {...props} key={o.code}
		                    style={{ ...props.style,
		                             background: isSuggested(o) ? dsColorNew.successBg : undefined }}>
		                  <Box sx={{ display: 'flex', width: '100%',
		                             alignItems: 'center', gap: 1 }}>
		                    {isSuggested(o) && (
		                      <CircleIcon sx={{ fontSize: 8, color: dsColorNew.successBar }}/>
		                    )}
		                    <Box component="code" sx={{ fontWeight: 700,
		                                                fontFamily: 'inherit',
		                                                fontSize: 11.5,
		                                                minWidth: 140 }}>
		                      {o.code}
		                    </Box>
		                    <Typography sx={{ fontSize: 12,
		                                      color: dsColorNew.textMuted }}>
		                      {o.name}
		                    </Typography>
		                  </Box>
		                </li>
		              )}
		              renderInput={(params) => (
		                <TextField {...params}
		                           placeholder="Select…"
		                           sx={inputSx}/>
		              )}/>
	)
}

/**
 * The pricelists more than one rate card writes, named as the operator
 * knows them. It replaced the amber "write conflicts" banner: sharing a
 * tariff is a legal configuration, not something attenuated, so this
 * informs — HelpNote tokens, no warning icon (UX obligatory 2,
 * CU-869ffwj71). The rule it states is the arbiter's: priority, then
 * the bigger move; the loser stays in the Activity as outranked.
 */
function SharedTariffsNote({ sharedWrites, pricelists }) {
	const nameOf = (code) => pricelists.find(p => p.code === code)?.name
	return (
		<AppNote severity="info" sx={{ mb: 2 }}>
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontWeight: 600, color: dsColorNew.noteInfo,
				                  fontSize: 13, mb: 0.5 }}>
					Shared write tariffs
				</Typography>
				{Object.entries(sharedWrites).map(([code, list]) => {
					const names = [...new Set(list.map(w => w.label))]
					return (
						<Typography key={code}
						            sx={{ color: dsColorNew.textDark, fontSize: 13, lineHeight: 1.6,
						                  overflow: 'hidden', textOverflow: 'ellipsis' }}
						            title={`${code}${nameOf(code) ? ` ${nameOf(code)}` : ''} — written by ${joinNames(names)}`}>
							<Box component="code" sx={{ fontWeight: 700 }}>{code}</Box>
							{nameOf(code) && <> <Box component="span" sx={{ color: dsColorNew.textMuted }}>{nameOf(code)}</Box></>}
							{' — written by '}
							{names.map((label, i) => (
								<React.Fragment key={label}>
									{i > 0 && (i === names.length - 1 ? ' and ' : ', ')}
									<Box component="span" sx={{ fontWeight: 700 }}>{label}</Box>
								</React.Fragment>
							))}
							.
						</Typography>
					)
				})}
				<Typography sx={{ color: dsColorNew.textDark, fontSize: 13, lineHeight: 1.6, mt: 0.5 }}>
					When two rate cards decide the same bracket of a shared tariff, the
					strategy with the higher priority sets the price; on a tie, the
					decision that moves the price more. The other decision stays in the
					Activity as <strong>outranked</strong>, never sent.
				</Typography>
			</Box>
		</AppNote>
	)
}

// The card's counts as the app's value chips next to its title: "Faces 11"
function KpiBox({ label, value }) {
	return (
		<AppValueChip tone="accent" sx={{ py: 0.6, fontSize: 12.5 }}>
			{label} <Box component="strong" sx={{ fontWeight: 600 }}>{value}</Box>
		</AppValueChip>
	)
}

function HeadCell({ children, ...rest }) {
	return (
		<TableCell {...rest}
		           sx={{ ...headCellSx, py: 1.25 }}>
			{children}
		</TableCell>
	)
}


/**
 * The tab needs the Price Seeker site catalogue for the site picker, the
 * site filter and the labels in the table, so it is provided once here
 * and read by every piece below through `useSiteCatalog()`.
 */
export default function RateCardsTab() {
	return (
		<SiteCatalogProvider>
			<RateCardsTabInner/>
		</SiteCatalogProvider>
	)
}
